import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Calendar, Clock, MapPin, Users, Plus, Edit2, Trash2, UserPlus, X, Bell, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_TYPES = ['Assembly', 'Delivery', 'Literacy Event', 'Community Outreach', 'Fundraiser', 'Training'];

const initialEventForm = {
  title: '',
  description: '',
  event_type: '',
  date: '',
  start_time: '',
  end_time: '',
  location: '',
  max_volunteers: 10
};

export default function EventManagement() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState(initialEventForm);
  const [submitting, setSubmitting] = useState(false);
  
  // Volunteer assignment state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [approvedVolunteers, setApprovedVolunteers] = useState([]);
  const [eventVolunteers, setEventVolunteers] = useState([]);
  const [selectedVolunteerId, setSelectedVolunteerId] = useState('');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await fetch(`${API}/events`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedVolunteers = async () => {
    try {
      const response = await fetch(`${API}/volunteers?status=Approved`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setApprovedVolunteers(data);
      }
    } catch (error) {
      console.error('Error fetching volunteers:', error);
    }
  };

  const fetchEventVolunteers = async (eventId) => {
    try {
      const response = await fetch(`${API}/events/${eventId}/volunteers`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEventVolunteers(data);
      }
    } catch (error) {
      console.error('Error fetching event volunteers:', error);
    }
  };

  const openAssignDialog = async (event) => {
    setSelectedEvent(event);
    setSelectedVolunteerId('');
    await fetchApprovedVolunteers();
    await fetchEventVolunteers(event.event_id);
    setShowAssignDialog(true);
  };

  const handleAssignVolunteer = async () => {
    if (!selectedVolunteerId || !selectedEvent) return;
    
    try {
      const response = await fetch(`${API}/events/${selectedEvent.event_id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ volunteer_id: selectedVolunteerId })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to assign volunteer');
      }
      
      toast.success('Volunteer assigned to event');
      await fetchEventVolunteers(selectedEvent.event_id);
      await fetchEvents();
      setSelectedVolunteerId('');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRemoveVolunteer = async (volunteerId) => {
    if (!selectedEvent) return;
    
    try {
      const response = await fetch(`${API}/events/${selectedEvent.event_id}/unregister`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ volunteer_id: volunteerId })
      });
      
      if (response.ok) {
        toast.success('Volunteer removed from event');
        await fetchEventVolunteers(selectedEvent.event_id);
        await fetchEvents();
      }
    } catch (error) {
      toast.error('Failed to remove volunteer');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const url = editingEvent 
        ? `${API}/events/${editingEvent.event_id}`
        : `${API}/events`;
      
      const method = editingEvent ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(eventForm)
      });

      if (response.ok) {
        toast.success(editingEvent ? 'Event updated successfully' : 'Event created successfully');
        fetchEvents();
        setShowDialog(false);
        setEventForm(initialEventForm);
        setEditingEvent(null);
      } else {
        toast.error('Failed to save event');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error('Failed to save event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setEventForm({
      title: event.title,
      description: event.description,
      event_type: event.event_type,
      date: event.date,
      start_time: event.start_time,
      end_time: event.end_time,
      location: event.location,
      max_volunteers: event.max_volunteers
    });
    setShowDialog(true);
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      const response = await fetch(`${API}/events/${eventId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Event deleted successfully');
        fetchEvents();
      } else {
        toast.error('Failed to delete event');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error('Failed to delete event');
    }
  };

  const sendReminder = async (eventId) => {
    try {
      const response = await fetch(`${API}/admin/events/${eventId}/send-reminder`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Reminders sent to ${data.reminders_sent} volunteers!`);
      } else {
        toast.error('Failed to send reminders');
      }
    } catch (error) {
      toast.error('Failed to send reminders');
    }
  };

  const sendAllReminders = async () => {
    try {
      const response = await fetch(`${API}/admin/events/send-reminders`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
      } else {
        toast.error('Failed to send reminders');
      }
    } catch (error) {
      toast.error('Failed to send reminders');
    }
  };

  const downloadCalendar = (eventId) => {
    window.open(`${API}/events/${eventId}/calendar`, '_blank');
  };

  const openNewEventDialog = () => {
    setEditingEvent(null);
    setEventForm(initialEventForm);
    setShowDialog(true);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isUpcoming = (dateStr) => {
    return new Date(dateStr) >= new Date(new Date().toDateString());
  };

  const upcomingEvents = events.filter(e => isUpcoming(e.date));
  const pastEvents = events.filter(e => !isUpcoming(e.date));

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="event-management">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Event Management
            </h1>
            <p className="text-slate-500 mt-1">Schedule and manage volunteer events</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline"
              onClick={sendAllReminders}
              className="border-[#5cc8e8] text-[#1a3a5c] hover:bg-[#5cc8e8]/10"
              data-testid="send-all-reminders-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Send Tomorrow's Reminders
            </Button>
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button 
                  onClick={openNewEventDialog}
                  className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                  data-testid="create-event-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Event
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    value={eventForm.title}
                    onChange={(e) => setEventForm(prev => ({ ...prev, title: e.target.value }))}
                    required
                    data-testid="event-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="event_type">Event Type *</Label>
                  <Select 
                    value={eventForm.event_type} 
                    onValueChange={(value) => setEventForm(prev => ({ ...prev, event_type: value }))}
                  >
                    <SelectTrigger data-testid="event-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={eventForm.description}
                    onChange={(e) => setEventForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    data-testid="event-description-input"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={eventForm.date}
                      onChange={(e) => setEventForm(prev => ({ ...prev, date: e.target.value }))}
                      required
                      data-testid="event-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="start_time">Start Time *</Label>
                    <Input
                      id="start_time"
                      type="time"
                      value={eventForm.start_time}
                      onChange={(e) => setEventForm(prev => ({ ...prev, start_time: e.target.value }))}
                      required
                      data-testid="event-start-time-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_time">End Time *</Label>
                    <Input
                      id="end_time"
                      type="time"
                      value={eventForm.end_time}
                      onChange={(e) => setEventForm(prev => ({ ...prev, end_time: e.target.value }))}
                      required
                      data-testid="event-end-time-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    value={eventForm.location}
                    onChange={(e) => setEventForm(prev => ({ ...prev, location: e.target.value }))}
                    required
                    data-testid="event-location-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_volunteers">Max Volunteers</Label>
                  <Input
                    id="max_volunteers"
                    type="number"
                    min="1"
                    value={eventForm.max_volunteers}
                    onChange={(e) => setEventForm(prev => ({ ...prev, max_volunteers: parseInt(e.target.value) }))}
                    data-testid="event-max-volunteers-input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting} className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90" data-testid="save-event-btn">
                    {submitting ? 'Saving...' : editingEvent ? 'Update Event' : 'Create Event'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <>
            {/* Upcoming Events */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-[#1a3a5c]">Upcoming Events ({upcomingEvents.length})</h2>
              {upcomingEvents.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {upcomingEvents.map((event) => (
                    <Card key={event.event_id} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <span className="px-2 py-1 bg-[#5cc8e8]/10 text-[#1a3a5c] text-xs font-medium rounded">
                            {event.event_type}
                          </span>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(event)} data-testid={`edit-event-${event.event_id}`}>
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(event.event_id)} data-testid={`delete-event-${event.event_id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        <h3 className="font-semibold text-[#1a3a5c] mb-2">{event.title}</h3>
                        <p className="text-sm text-slate-500 mb-4 line-clamp-2">{event.description}</p>
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2 text-slate-600">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {formatDate(event.date)}
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Clock className="w-4 h-4 text-slate-400" />
                            {event.start_time} - {event.end_time}
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            {event.location}
                          </div>
                          <div className="flex items-center gap-2 text-slate-600">
                            <Users className="w-4 h-4 text-slate-400" />
                            {event.registered_volunteers?.length || 0} / {event.max_volunteers} volunteers
                          </div>
                        </div>
                        <div className="flex gap-2 mt-4">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1"
                            onClick={() => openAssignDialog(event)}
                            data-testid={`assign-volunteers-${event.event_id}`}
                          >
                            <UserPlus className="w-4 h-4 mr-1" />
                            Volunteers
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => sendReminder(event.event_id)}
                            title="Send reminder emails"
                            data-testid={`send-reminder-${event.event_id}`}
                          >
                            <Bell className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => downloadCalendar(event.event_id)}
                            title="Download calendar file"
                            data-testid={`download-calendar-${event.event_id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card className="border-0 shadow-md">
                  <CardContent className="p-8 text-center text-slate-500">
                    <Calendar className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No upcoming events scheduled</p>
                    <Button onClick={openNewEventDialog} className="mt-4" variant="outline">
                      Schedule an Event
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Past Events */}
            {pastEvents.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-[#1a3a5c]">Past Events ({pastEvents.length})</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-75">
                  {pastEvents.slice(0, 6).map((event) => (
                    <Card key={event.event_id} className="border-0 shadow-sm">
                      <CardContent className="p-5">
                        <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded">
                          {event.event_type}
                        </span>
                        <h3 className="font-semibold text-slate-700 mt-2 mb-1">{event.title}</h3>
                        <p className="text-sm text-slate-500">{formatDate(event.date)}</p>
                        <p className="text-sm text-slate-500">{event.registered_volunteers?.length || 0} volunteers attended</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Volunteer Assignment Dialog */}
        <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Manage Volunteers - {selectedEvent?.title}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-sm text-slate-500">
                {selectedEvent?.date} | {selectedEvent?.start_time} - {selectedEvent?.end_time}
              </div>
              
              {/* Assign new volunteer */}
              <div className="space-y-2">
                <Label>Add Volunteer</Label>
                <div className="flex gap-2">
                  <Select value={selectedVolunteerId} onValueChange={setSelectedVolunteerId}>
                    <SelectTrigger className="flex-1" data-testid="select-volunteer-dropdown">
                      <SelectValue placeholder="Select an approved volunteer" />
                    </SelectTrigger>
                    <SelectContent>
                      {approvedVolunteers
                        .filter(v => !eventVolunteers.some(ev => ev.volunteer_id === v.volunteer_id))
                        .map((volunteer) => (
                          <SelectItem key={volunteer.volunteer_id} value={volunteer.volunteer_id}>
                            {volunteer.first_name} {volunteer.last_name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={handleAssignVolunteer}
                    disabled={!selectedVolunteerId}
                    className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90"
                    data-testid="assign-volunteer-btn"
                  >
                    <UserPlus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-slate-400">
                  Only approved volunteers can be assigned. Time conflicts will be checked automatically.
                </p>
              </div>
              
              {/* Current volunteers */}
              <div className="space-y-2">
                <Label>Assigned Volunteers ({eventVolunteers.length}/{selectedEvent?.max_volunteers})</Label>
                {eventVolunteers.length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center bg-slate-50 rounded-lg">
                    No volunteers assigned yet
                  </p>
                ) : (
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {eventVolunteers.map((volunteer) => (
                      <div key={volunteer.volunteer_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{volunteer.first_name} {volunteer.last_name}</p>
                          <p className="text-xs text-slate-500">{volunteer.email}</p>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveVolunteer(volunteer.volunteer_id)}
                          data-testid={`remove-volunteer-${volunteer.volunteer_id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowAssignDialog(false)} 
                  className="w-full"
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
